/**
 * Workspace Documents Tab - 청구서/영수증 관리
 * 
 * Phase 1 구현:
 * - 청구서 목록 로드 (billing_documents)
 * - 청구서 생성 다이얼로그
 * - 상태 변경 (draft → issued → paid)
 * - 영수증 생성 (입금 확인 시)
 * - PDF 다운로드 (추후)
 */

import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import {
    FileText, Loader2, Plus, MoreHorizontal,
    CheckCircle, Clock, AlertCircle, Ban,
    Download, Receipt, Eye
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../components/ui/table';
import { Fire } from '../../../services/FireClient';
import { AdminFire } from '../../../services/SuperAdminFirebase';
import { collection, getDocs, query, orderBy, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { BillingDocumentType, BillingDocumentStatus } from '../../../models/BillingDocument';
import CreateInvoiceDialog from '../components/CreateInvoiceDialog';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../../../components/ui/dialog';

interface Props {
    workspaceId: string;
    refreshKey?: number;
}

interface DocumentRecord {
    id: string;
    type: BillingDocumentType;
    invoiceNumber?: string;
    total: number;
    currency: string;
    status: BillingDocumentStatus;
    issueDate: Date;
    dueDate?: Date;
    paidDate?: Date;
    createdAt: Date;
    recipientName?: string;
}

const WorkspaceDocumentsTab: React.FC<Props> = ({ workspaceId, refreshKey }) => {
    const { i18n } = useLingui();
    const [loading, setLoading] = useState(true);
    const [documents, setDocuments] = useState<DocumentRecord[]>([]);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [filter, setFilter] = useState<'all' | BillingDocumentType>('all');

    // 상세 보기
    const [selectedDoc, setSelectedDoc] = useState<any | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);

    useEffect(() => {
        loadDocuments();
    }, [workspaceId, refreshKey]);

    const loadDocuments = async () => {
        setLoading(true);
        try {
            const q = query(
                collection(Fire.store, 'workspaces', workspaceId, 'billing_documents'),
                orderBy('createdAt', 'desc')
            );
            const snap = await getDocs(q);

            const docs: DocumentRecord[] = snap.docs.map(d => {
                const data = d.data();
                return {
                    id: d.id,
                    type: data.type || 'INVOICE',
                    invoiceNumber: data.invoiceNumber || d.id,
                    total: data.total || 0,
                    currency: data.currency || 'JPY',
                    status: data.status || 'draft',
                    issueDate: data.issueDate?.toDate?.() || new Date(),
                    dueDate: data.dueDate?.toDate?.() || undefined,
                    paidDate: data.paidDate?.toDate?.() || undefined,
                    createdAt: data.createdAt?.toDate?.() || new Date(),
                    recipientName: data.recipient?.companyName || '',
                };
            });

            setDocuments(docs);
        } catch (e) {
            console.error('Failed to load documents:', e);
        } finally {
            setLoading(false);
        }
    };

    // 상태 변경
    const handleStatusChange = async (docId: string, newStatus: BillingDocumentStatus) => {
        try {
            const docRef = doc(Fire.store, 'workspaces', workspaceId, 'billing_documents', docId);
            const updateData: any = {
                status: newStatus,
                updatedAt: serverTimestamp(),
            };

            if (newStatus === 'paid') {
                updateData.paidDate = serverTimestamp();
            }

            await updateDoc(docRef, updateData);
            await loadDocuments();
        } catch (e) {
            console.error('Failed to update status:', e);
            alert(t(i18n)`ステータス更新に失敗しました`);
        }
    };

    // 영수증 생성 (paid로 변경 후 자동 생성)
    const handleCreateReceipt = async (invoiceDoc: DocumentRecord) => {
        if (invoiceDoc.status !== 'paid') {
            const confirm = window.confirm(t(i18n)`この請求書の入金を確認して領収書を発行しますか？`);
            if (!confirm) return;
        }

        try {
            // 청구서를 paid로 변경
            await handleStatusChange(invoiceDoc.id, 'paid');

            // TODO: 영수증 문서 생성 (별도 문서로)
            // 현재는 청구서의 paidDate만 설정

            alert(t(i18n)`領収書が発行されました`);
        } catch (e) {
            console.error('Failed to create receipt:', e);
        }
    };

    // PDF 다운로드
    const [pdfLoading, setPdfLoading] = useState<string | null>(null);
    const handleDownloadPDF = async (docRecord: DocumentRecord) => {
        setPdfLoading(docRecord.id);
        try {
            const generateBillingPDF = AdminFire.getFunc('generateBillingPDF');
            const result = await generateBillingPDF({
                workspaceId,
                documentId: docRecord.id,
            });

            const data = result.data as { success: boolean; url?: string };
            if (data.success && data.url) {
                window.open(data.url, '_blank');
            } else {
                throw new Error('PDF generation failed');
            }
        } catch (e) {
            console.error('Failed to generate PDF:', e);
            alert(t(i18n)`PDF生成に失敗しました`);
        } finally {
            setPdfLoading(null);
        }
    };

    // 상세 보기
    const handleViewDetail = async (docRecord: DocumentRecord) => {
        setDetailLoading(true);
        try {
            const { getDoc } = await import('firebase/firestore');
            const docRef = doc(Fire.store, 'workspaces', workspaceId, 'billing_documents', docRecord.id);
            const snap = await getDoc(docRef);
            if (snap.exists()) {
                setSelectedDoc({
                    id: snap.id,
                    ...snap.data(),
                    issueDate: snap.data().issueDate?.toDate?.() || null,
                    dueDate: snap.data().dueDate?.toDate?.() || null,
                    paidDate: snap.data().paidDate?.toDate?.() || null,
                    createdAt: snap.data().createdAt?.toDate?.() || null,
                });
            }
        } catch (e) {
            console.error('Failed to load document detail:', e);
        } finally {
            setDetailLoading(false);
        }
    };

    // 필터링된 문서
    const filteredDocuments = filter === 'all'
        ? documents
        : documents.filter(d => d.type === filter);

    // 통계
    const stats = {
        total: documents.length,
        draft: documents.filter(d => d.status === 'draft').length,
        issued: documents.filter(d => d.status === 'issued').length,
        paid: documents.filter(d => d.status === 'paid').length,
        unpaidAmount: documents
            .filter(d => d.status === 'issued')
            .reduce((sum, d) => sum + d.total, 0),
    };

    const formatCurrency = (amount: number, currency: string = 'JPY') => {
        return new Intl.NumberFormat('ja-JP', { style: 'currency', currency }).format(amount);
    };

    const formatDate = (date: Date | undefined) => {
        if (!date) return '-';
        return new Intl.DateTimeFormat('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).format(date);
    };

    const getStatusBadge = (status: BillingDocumentStatus) => {
        switch (status) {
            case 'draft':
                return <Badge variant="secondary" className="bg-slate-100 text-slate-600"><Clock className="w-3 h-3 mr-1" /><Trans>下書き</Trans></Badge>;
            case 'issued':
                return <Badge variant="secondary" className="bg-amber-100 text-amber-700"><AlertCircle className="w-3 h-3 mr-1" /><Trans>発行済み</Trans></Badge>;
            case 'paid':
                return <Badge variant="secondary" className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" /><Trans>入金済み</Trans></Badge>;
            case 'canceled':
                return <Badge variant="secondary" className="bg-red-100 text-red-700"><Ban className="w-3 h-3 mr-1" /><Trans>キャンセル</Trans></Badge>;
            default:
                return <Badge variant="secondary">{status}</Badge>;
        }
    };

    const getTypeBadge = (type: BillingDocumentType) => {
        switch (type) {
            case 'INVOICE':
                return <Badge variant="outline" className="border-indigo-200 text-indigo-600"><Trans>請求書</Trans></Badge>;
            case 'RECEIPT':
                return <Badge variant="outline" className="border-emerald-200 text-emerald-600"><Trans>領収書</Trans></Badge>;
            case 'ESTIMATE':
                return <Badge variant="outline" className="border-slate-200 text-slate-600"><Trans>見積書</Trans></Badge>;
            default:
                return <Badge variant="outline">{type}</Badge>;
        }
    };

    if (loading) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-4">
            {/* 통계 카드 */}
            <div className="grid grid-cols-4 gap-4">
                <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('all')}>
                    <CardContent className="pt-4">
                        <div className="text-2xl font-bold">{stats.total}</div>
                        <div className="text-sm text-slate-500"><Trans>全文書</Trans></div>
                    </CardContent>
                </Card>
                <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('INVOICE')}>
                    <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-amber-600">{stats.issued}</div>
                        <div className="text-sm text-slate-500"><Trans>未入金</Trans></div>
                    </CardContent>
                </Card>
                <Card className="cursor-pointer hover:shadow-md transition-shadow">
                    <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-emerald-600">{stats.paid}</div>
                        <div className="text-sm text-slate-500"><Trans>入金済み</Trans></div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-red-600">{formatCurrency(stats.unpaidAmount)}</div>
                        <div className="text-sm text-slate-500"><Trans>未収金額</Trans></div>
                    </CardContent>
                </Card>
            </div>

            {/* 문서 목록 */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="text-lg"><Trans>請求書・領収書</Trans></CardTitle>
                        <CardDescription>
                            {filter === 'all'
                                ? <Trans>すべての文書</Trans>
                                : filter === 'INVOICE'
                                    ? <Trans>請求書のみ</Trans>
                                    : <Trans>領収書のみ</Trans>
                            }
                        </CardDescription>
                    </div>
                    <Button
                        className="bg-indigo-600 hover:bg-indigo-700"
                        onClick={() => setShowCreateDialog(true)}
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        <Trans>請求書を作成</Trans>
                    </Button>
                </CardHeader>
                <CardContent>
                    {filteredDocuments.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                            <p><Trans>文書がありません</Trans></p>
                            <p className="text-sm mt-1"><Trans>請求書を作成してください</Trans></p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead><Trans>文書番号</Trans></TableHead>
                                    <TableHead><Trans>種類</Trans></TableHead>
                                    <TableHead><Trans>請求先</Trans></TableHead>
                                    <TableHead className="text-right"><Trans>金額</Trans></TableHead>
                                    <TableHead><Trans>発行日</Trans></TableHead>
                                    <TableHead><Trans>支払期限</Trans></TableHead>
                                    <TableHead><Trans>ステータス</Trans></TableHead>
                                    <TableHead className="w-10"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredDocuments.map(doc => (
                                    <TableRow key={doc.id} className="hover:bg-slate-50">
                                        <TableCell className="font-mono text-sm">
                                            {doc.invoiceNumber}
                                        </TableCell>
                                        <TableCell>
                                            {getTypeBadge(doc.type)}
                                        </TableCell>
                                        <TableCell className="max-w-[150px] truncate">
                                            {doc.recipientName || '-'}
                                        </TableCell>
                                        <TableCell className="text-right font-medium">
                                            {formatCurrency(doc.total, doc.currency)}
                                        </TableCell>
                                        <TableCell className="text-sm text-slate-600">
                                            {formatDate(doc.issueDate)}
                                        </TableCell>
                                        <TableCell className="text-sm text-slate-600">
                                            {formatDate(doc.dueDate)}
                                        </TableCell>
                                        <TableCell>
                                            {getStatusBadge(doc.status)}
                                        </TableCell>
                                        <TableCell>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon">
                                                        <MoreHorizontal className="w-4 h-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={() => handleViewDetail(doc)}>
                                                        <Eye className="w-4 h-4 mr-2" />
                                                        <Trans>詳細を見る</Trans>
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        onClick={() => handleDownloadPDF(doc)}
                                                        disabled={pdfLoading === doc.id}
                                                    >
                                                        {pdfLoading === doc.id ? (
                                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        ) : (
                                                            <Download className="w-4 h-4 mr-2" />
                                                        )}
                                                        <Trans>PDFダウンロード</Trans>
                                                    </DropdownMenuItem>
                                                    <DropdownMenuSeparator />

                                                    {doc.status === 'draft' && (
                                                        <DropdownMenuItem onClick={() => handleStatusChange(doc.id, 'issued')}>
                                                            <AlertCircle className="w-4 h-4 mr-2" />
                                                            <Trans>発行する</Trans>
                                                        </DropdownMenuItem>
                                                    )}

                                                    {doc.status === 'issued' && (
                                                        <>
                                                            <DropdownMenuItem onClick={() => handleCreateReceipt(doc)}>
                                                                <Receipt className="w-4 h-4 mr-2" />
                                                                <Trans>入金確認・領収書発行</Trans>
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem
                                                                onClick={() => handleStatusChange(doc.id, 'canceled')}
                                                                className="text-red-600"
                                                            >
                                                                <Ban className="w-4 h-4 mr-2" />
                                                                <Trans>キャンセル</Trans>
                                                            </DropdownMenuItem>
                                                        </>
                                                    )}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* 청구서 생성 다이얼로그 */}
            <CreateInvoiceDialog
                open={showCreateDialog}
                onOpenChange={setShowCreateDialog}
                workspaceId={workspaceId}
                onCreated={loadDocuments}
            />

            {/* 상세 보기 다이얼로그 */}
            <Dialog open={!!selectedDoc} onOpenChange={(open) => !open && setSelectedDoc(null)}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {selectedDoc?.type === 'INVOICE' ? '請求書' : '領収書'} 詳細
                        </DialogTitle>
                        <DialogDescription>
                            {selectedDoc?.invoiceNumber || selectedDoc?.receiptNumber}
                        </DialogDescription>
                    </DialogHeader>

                    {detailLoading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                        </div>
                    ) : selectedDoc && (
                        <div className="space-y-6">
                            {/* 기본 정보 */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <div className="text-xs text-slate-500">ステータス</div>
                                    <div>{getStatusBadge(selectedDoc.status)}</div>
                                </div>
                                <div className="space-y-1">
                                    <div className="text-xs text-slate-500">金額</div>
                                    <div className="text-xl font-bold">
                                        {formatCurrency(selectedDoc.total, selectedDoc.currency)}
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <div className="text-xs text-slate-500">発行日</div>
                                    <div>{formatDate(selectedDoc.issueDate)}</div>
                                </div>
                                {selectedDoc.dueDate && (
                                    <div className="space-y-1">
                                        <div className="text-xs text-slate-500">支払期限</div>
                                        <div>{formatDate(selectedDoc.dueDate)}</div>
                                    </div>
                                )}
                                {selectedDoc.paidDate && (
                                    <div className="space-y-1">
                                        <div className="text-xs text-slate-500">入金日</div>
                                        <div>{formatDate(selectedDoc.paidDate)}</div>
                                    </div>
                                )}
                            </div>

                            {/* 발행자 정보 */}
                            {selectedDoc.issuer && (
                                <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                                    <div className="font-medium text-slate-700">発行者</div>
                                    <div className="text-sm text-slate-600">
                                        {selectedDoc.issuer.companyName && <div>{selectedDoc.issuer.companyName}</div>}
                                        {selectedDoc.issuer.representative && <div>{selectedDoc.issuer.representative}</div>}
                                        {selectedDoc.issuer.address && <div className="text-slate-500">{selectedDoc.issuer.address}</div>}
                                        {selectedDoc.issuer.registrationNumber && (
                                            <div className="text-xs text-slate-400 mt-1">
                                                登録番号: {selectedDoc.issuer.registrationNumber}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* 수신자 정보 */}
                            {selectedDoc.recipient && (
                                <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                                    <div className="font-medium text-slate-700">宛先</div>
                                    <div className="text-sm text-slate-600">
                                        {selectedDoc.recipient.companyName && <div>{selectedDoc.recipient.companyName}</div>}
                                        {selectedDoc.recipient.department && <div>{selectedDoc.recipient.department}</div>}
                                        {selectedDoc.recipient.address && <div className="text-slate-500">{selectedDoc.recipient.address}</div>}
                                    </div>
                                </div>
                            )}

                            {/* 품목 */}
                            {selectedDoc.items && selectedDoc.items.length > 0 && (
                                <div className="space-y-2">
                                    <div className="font-medium text-slate-700">明細</div>
                                    <table className="w-full text-sm">
                                        <thead className="bg-slate-100">
                                            <tr>
                                                <th className="text-left p-2">品目</th>
                                                <th className="text-right p-2">数量</th>
                                                <th className="text-right p-2">単価</th>
                                                <th className="text-right p-2">金額</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedDoc.items.map((item: any, idx: number) => (
                                                <tr key={idx} className="border-b border-slate-100">
                                                    <td className="p-2">{item.description}</td>
                                                    <td className="text-right p-2">{item.quantity}</td>
                                                    <td className="text-right p-2">{formatCurrency(item.unitPrice, selectedDoc.currency)}</td>
                                                    <td className="text-right p-2">{formatCurrency(item.amount, selectedDoc.currency)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="font-medium">
                                            <tr>
                                                <td colSpan={3} className="text-right p-2">小計</td>
                                                <td className="text-right p-2">{formatCurrency(selectedDoc.subtotal || 0, selectedDoc.currency)}</td>
                                            </tr>
                                            <tr>
                                                <td colSpan={3} className="text-right p-2">消費税 ({(selectedDoc.taxRate || 0.1) * 100}%)</td>
                                                <td className="text-right p-2">{formatCurrency(selectedDoc.taxAmount || 0, selectedDoc.currency)}</td>
                                            </tr>
                                            <tr className="text-lg">
                                                <td colSpan={3} className="text-right p-2">合計</td>
                                                <td className="text-right p-2">{formatCurrency(selectedDoc.total, selectedDoc.currency)}</td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}

                            {/* PDF 다운로드 버튼 */}
                            <div className="flex justify-end gap-2 pt-4 border-t">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        handleDownloadPDF({ id: selectedDoc.id } as DocumentRecord);
                                    }}
                                    disabled={pdfLoading === selectedDoc.id}
                                >
                                    {pdfLoading === selectedDoc.id ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Download className="w-4 h-4 mr-2" />
                                    )}
                                    PDFダウンロード
                                </Button>
                                <Button variant="default" onClick={() => setSelectedDoc(null)}>
                                    閉じる
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default WorkspaceDocumentsTab;
